import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'Src/helpers/api/axiosInstance';
import { Widget } from '../../../../components/Widget/Widget';
import './WhatsNewWidget.css';

type ISODate = string;

interface NewsItem {
  id: number | string;
  title: string;
  content: string;
  activeDate?: ISODate;
  endDate?: ISODate;
  priority?: number;
  badge?: string;
  image?: string;
  ctaText?: string;
  ctaHref?: string;
  iconKey?: string;
}

type WhatsNewWidgetProps = {
  onRemove: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
};

const DISMISSED_IDS_KEY = 'whatsnew_dismiss_ids';
const OPENED_IDS_KEY = 'whatsnew_opened_ids';

function isActiveToday(item: NewsItem, today: string) {
  const starts = item.activeDate || '0000-01-01';
  const ends = item.endDate || '9999-12-31';
  return starts <= today && today <= ends;
}

function shuffle<T>(arr: T[]) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = a[i];
    a[i] = a[j];
    a[j] = t;
  }
  return a;
}

function readIdSetFromLocal(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set<string>();
    const arr = JSON.parse(raw) as Array<string | number>;
    return new Set<string>(arr.map(String));
  } catch {
    return new Set<string>();
  }
}

function writeIdSetToLocal(key: string, set: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {}
}

function readIdSetFromSession(key: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return new Set<string>();
    const arr = JSON.parse(raw) as Array<string | number>;
    return new Set<string>(arr.map(String));
  } catch {
    return new Set<string>();
  }
}

function writeIdSetToSession(key: string, set: Set<string>) {
  try {
    sessionStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {}
}

function renderWidgetIcon(iconKey?: string): React.ReactNode {
  switch (String(iconKey || '').trim()) {
    case 'bell':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9a6 6 0 1 1 12 0c0 4.5 1.5 5.5 2 6H4c.5-.5 2-1.5 2-6" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
      );

    case 'trophy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
          <path d="M8 5H5a2 2 0 0 0 2 4h1" />
          <path d="M16 5h3a2 2 0 0 1-2 4h-1" />
          <path d="M12 11v4" />
          <path d="M9 20h6" />
          <path d="M8 20h8" />
        </svg>
      );

    case 'shirt':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 5 12 7l3-2 4 3-2 3-2-1v9H9v-9l-2 1-2-3 4-3z" />
        </svg>
      );

    case 'badge':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M9.5 12.5 8 20l4-2 4 2-1.5-7.5" />
        </svg>
      );

    case 'media':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="m20 15-4.5-4.5L8 18" />
        </svg>
      );

    case 'rocket':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14 4c3 1 5 3 6 6-2.5 2.5-5.5 4.5-9 6-1.5-3.5-3.5-6.5-6-9 3-3 6-4 9-3Z" />
          <path d="M10 14 6 18" />
          <path d="M8 16l-2 2" />
        </svg>
      );

    case 'home':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 11.5 12 5l8 6.5" />
          <path d="M6.5 10.5V19h11v-8.5" />
        </svg>
      );

    case 'chart':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 19V9" />
          <path d="M12 19V5" />
          <path d="M19 19v-7" />
          <path d="M4 19h16" />
        </svg>
      );

    case 'megaphone':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 11v2" />
          <path d="M6 10h3l8-4v12l-8-4H6z" />
          <path d="M6 14l1.5 4h2" />
        </svg>
      );

    case 'search':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6" />
          <path d="m20 20-4.2-4.2" />
        </svg>
      );

    case 'clipboard':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="6" y="5" width="12" height="15" rx="2" />
          <path d="M9 5.5h6" />
          <path d="M9 10h6" />
          <path d="M9 14h4" />
        </svg>
      );

    case 'note':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 4h10l3 3v13H7z" />
          <path d="M17 4v4h4" />
          <path d="M10 12h6" />
          <path d="M10 16h4" />
        </svg>
      );

    case 'flag':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 21V4" />
          <path d="M6 5h10l-2 3 2 3H6" />
        </svg>
      );

    case 'check':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m5 12 4 4 10-10" />
        </svg>
      );

    case 'list':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 7h10" />
          <path d="M9 12h10" />
          <path d="M9 17h10" />
          <circle cx="5" cy="7" r="1" />
          <circle cx="5" cy="12" r="1" />
          <circle cx="5" cy="17" r="1" />
        </svg>
      );

    case 'calendar':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 3v3M16 3v3M4 9h16" />
          <rect x="4" y="5" width="16" height="15" rx="3" ry="3" />
        </svg>
      );

    case 'status':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="6" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="18" cy="12" r="2" />
        </svg>
      );

    default:
      return null;
  }
}

export function WhatsNewWidget({ onRemove, dragHandleProps }: WhatsNewWidgetProps) {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [openItem, setOpenItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readIdSetFromLocal(DISMISSED_IDS_KEY));
  const [openedIds, setOpenedIds] = useState<Set<string>>(() => readIdSetFromSession(OPENED_IDS_KEY));
  const [showAll, setShowAll] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = (await axios.get('/dist/images/whatsnew.json', { timeout: 10000 })) as { data: NewsItem[] };
        if (!mounted) return;
        setItems(Array.isArray(resp.data) ? resp.data : []);
      } catch {
        if (!mounted) return;
        setErr('Unable to load What’s New.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const active = useMemo(() => items.filter(i => isActiveToday(i, today)), [items, today]);

  const prioritized = useMemo<NewsItem[]>(() => {
    const grouped: Record<number, NewsItem[]> = {};
    for (const it of active) {
      const p = it.priority != null ? it.priority : 0;
      if (!grouped[p]) grouped[p] = [];
      grouped[p].push(it);
    }
    const priorities = Object.keys(grouped)
      .map(n => parseInt(n, 10))
      .sort((a, b) => b - a);

    const flat: NewsItem[] = [];
    for (const p of priorities) {
      const group = grouped[p];
      const mixed = shuffle(group);
      for (const item of mixed) flat.push(item);
    }
    return flat;
  }, [active]);

  const visible = useMemo<NewsItem[]>(() => {
    const out: NewsItem[] = [];
    for (const it of prioritized) {
      if (!dismissedIds.has(String(it.id))) out.push(it);
      if (out.length === 3) break;
    }
    return out;
  }, [prioritized, dismissedIds]);

  const allVisible = useMemo<NewsItem[]>(() => {
    return prioritized.filter(it => !dismissedIds.has(String(it.id)));
  }, [prioritized, dismissedIds]);

  useEffect(() => {
    if (!openItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openItem]);

  const closeAndMarkOpenedThisSession = () => {
    if (!openItem) return;
    const id = String(openItem.id);
    const next = new Set<string>(openedIds);
    next.add(id);
    setOpenedIds(next);
    writeIdSetToSession(OPENED_IDS_KEY, next);
    setOpenItem(null);
  };

  const dontShowAgainForThisItem = () => {
    if (!openItem) return;
    const id = String(openItem.id);

    const dismissedNext = new Set<string>(dismissedIds);
    dismissedNext.add(id);
    setDismissedIds(dismissedNext);
    writeIdSetToLocal(DISMISSED_IDS_KEY, dismissedNext);

    const openedNext = new Set<string>(openedIds);
    openedNext.add(id);
    setOpenedIds(openedNext);
    writeIdSetToSession(OPENED_IDS_KEY, openedNext);

    setOpenItem(null);
  };

  const isExternalHref = (href: string) => /^https?:\/\//i.test(href);

  const modalNode = openItem ? (
    <div className="wn-modal-backdrop" role="dialog" aria-modal="true" onClick={closeAndMarkOpenedThisSession}>
      <div className="wn-modal" onClick={e => e.stopPropagation()}>
        <div className="wn-modal-header">
          <h3>{openItem.title}</h3>
          <button className="wn-close" onClick={closeAndMarkOpenedThisSession} aria-label="Close">
            ×
          </button>
        </div>
        <div className="wn-modal-content" dangerouslySetInnerHTML={{ __html: openItem.content }} />
        <div className="wn-modal-footer">
          {openItem.ctaHref &&
            (isExternalHref(openItem.ctaHref) ? (
              <a className="wn-cta" href={openItem.ctaHref} rel="noopener noreferrer" target="_blank">
                {openItem.ctaText || 'Learn more'}
              </a>
            ) : (
              <Link className="wn-cta" to={openItem.ctaHref} onClick={closeAndMarkOpenedThisSession}>
                {openItem.ctaText || 'Learn more'}
              </Link>
            ))}
          <button className="wn-secondary" onClick={dontShowAgainForThisItem}>
            Don’t show again
          </button>
          <button className="wn-secondary" onClick={closeAndMarkOpenedThisSession}>
            Close
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const allItemsModalNode = showAll ? (
    <div className="wn-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setShowAll(false)}>
      <div className="wn-modal wn-modal--all" onClick={e => e.stopPropagation()}>
        <div className="wn-modal-header">
          <h3>All updates</h3>
          <button className="wn-close" onClick={() => setShowAll(false)} aria-label="Close">
            ×
          </button>
        </div>
        <div className="wn-all-list">
          {allVisible.map(item => {
            const badgeKey = (item.badge || '').trim().toLowerCase();
            return (
              <button
                key={String(item.id)}
                className="wn-all-item"
                onClick={() => {
                  setShowAll(false);
                  setOpenItem(item);
                }}
                type="button"
              >
                <span className="wn-all-item-icon">{item.iconKey ? renderWidgetIcon(item.iconKey) : null}</span>
                <span className="wn-all-item-body">
                  <span className="wn-all-item-title">{item.title}</span>
                  <span className="wn-all-item-kicker">Tap to see what's inside →</span>
                </span>
                {badgeKey && (
                  <span className="wn-badge" data-badge={badgeKey}>
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <Widget
        title={
          <span className="whatsnew-title">
            <span className="whatsnew-titleIcon">
              <svg viewBox="0 0 24 24" aria-hidden="true" width="16" height="16">
                <path d="M12 3c0 4-4 6-4 10a4 4 0 0 0 8 0c0-4-4-6-4-10z" />
                <path d="M12 17v1" />
              </svg>
            </span>
            What's New
          </span>
        }
        onRemove={onRemove}
        dragHandleProps={dragHandleProps}
      >
        <div className="whatsnew wn-compact">
          {loading && <div className="wn-loading">Loading…</div>}
          {err && <div className="wn-error">{err}</div>}
          {!loading && !err && visible.length === 0 && (
            <div className="wn-empty">No new highlights today. Check back soon!</div>
          )}
          <div className="wn-grid">
            {visible.map(item => {
              const badgeKey = (item.badge || '').trim().toLowerCase();
              return (
                <button
                  key={String(item.id)}
                  className="wn-card"
                  onClick={() => setOpenItem(item)}
                  aria-label={`Open: ${item.title}`}
                  type="button"
                >
                  {badgeKey && (
                    <span
                      className="wn-badge wn-badge--corner"
                      data-badge={badgeKey}
                      aria-label={`Badge: ${item.badge}`}
                    >
                      {item.badge}
                    </span>
                  )}
                  {item.image && <div className="wn-img" style={{ backgroundImage: `url(${item.image})` }} />}
                  <div className="wn-burst" aria-hidden="true" />
                  <div className="wn-card-body">
                    <div className="wn-cardTitleRow">
                      {item.iconKey ? <span className="wn-cardIcon">{renderWidgetIcon(item.iconKey)}</span> : null}
                      <h4 className="wn-card-title">{item.title}</h4>
                    </div>
                    <p className="wn-card-kicker">Tap to see what’s inside →</p>
                  </div>
                </button>
              );
            })}
          </div>
          {allVisible.length > 3 && (
            <button className="wn-view-more" onClick={() => setShowAll(true)} type="button">
              View all updates ({allVisible.length})
            </button>
          )}
        </div>
      </Widget>

      {modalNode ? createPortal(modalNode, document.body) : null}
      {allItemsModalNode ? createPortal(allItemsModalNode, document.body) : null}
    </>
  );
}
